import React, { useCallback, useState } from 'react';
import { SerializedSmartLink } from 'fym-common/src/db/serializersTypes';
import { useDeleteSmartLinkMutation } from '../../redux/api/fymAPI';
import { SMLDeletePageModal } from './deletePage/SMLDeletePageModal';
import { isSuccessfulResult } from '../../redux/api/utils/isSuccessfulResult';
import { useSMLRouter } from '../../routing/sml/hooks';
import { SMLRoutes } from '../../routing/sml/SMLRoutes';

/**
 * Deleting a page, and saying what that costs before it happens.
 *
 * The page itself is passed in because the dialog reports on it: its name, its
 * address, the pre-saves it owes and the traffic it has gathered. Without it
 * the dialog can only ask "are you sure?", which is the question this replaced.
 */
export function useDeleteSmartLinkFlow(
  accountId: number,
  smartLinkId: number | undefined,
  smartLink?: SerializedSmartLink
) {
  const router = useSMLRouter();
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

  const [deleteSmartLink, { isLoading: isDeleteSmartLinkLoading, error: deleteSmartLinkError, reset }] =
    useDeleteSmartLinkMutation();

  const openModal = useCallback(() => {
    reset();
    setIsModalOpen(true);
  }, [reset]);
  const closeModal = useCallback(() => setIsModalOpen(false), []);

  const onDelete = useCallback(() => {
    if (smartLinkId === undefined) {
      return;
    }
    // The dialog stays open with a spinner until the delete ends, and shows a failure inside it
    // (design dialogs a4, a5).
    deleteSmartLink({ accountId, smartLinkId })
      .then((result) => {
        if (!isSuccessfulResult(result)) {
          return;
        }
        closeModal();
        router.push(SMLRoutes.smartlinks);
      })
      .catch(console.error);
  }, [deleteSmartLink, accountId, smartLinkId, router, closeModal]);

  return {
    openSmartLinkDeletionModal: openModal,
    isDeleteSmartLinkLoading,
    deleteSmartLinkError,
    SmartLinkDeletionModal: (
      <SMLDeletePageModal
        isOpen={isModalOpen}
        accountId={accountId}
        smartLink={smartLink}
        closeModal={closeModal}
        onDelete={onDelete}
        isDeleting={isDeleteSmartLinkLoading}
        error={deleteSmartLinkError}
      />
    ),
  };
}
